import React, { useRef, useEffect, useCallback } from "react";
import { Container } from "@pixi/react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useDimensions } from "../../../Contexts/DimensionsContext";
import Overlay from "../../../Games/Shared/Overlay/Overlay";
import HoldAndWinIntroController from "./HoldAndWinIntroController";
import TextButton from "../../UserInterface/primary/Buttons/TextButton";
import { safeInit } from "../../../utilities";
import { selectShowHoldAndWinIntro } from "../../../State/selectors";
import { SOUND_NAME } from "../../../sounds";
import { soundManager } from "../../../SoundManager";

const HoldAndWinIntroDialog = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const showHoldAndWinIntro = useSelector(selectShowHoldAndWinIntro);

  const introControllerRef = useRef(null);
  const spineContainerRef = useRef(null);
  const overlayRef = useRef(null);
  const buttonRef = useRef(null);
  const dimensionsContext = useDimensions();
  const { dimensions, symbolHeight } = dimensionsContext;

  // Initialize controller once
  useEffect(() => {
    if (spineContainerRef.current && !introControllerRef.current) {
      // Set button alpha to 0 for this instance
      if (buttonRef.current) {
        buttonRef.current.alpha = 0;
      }

      safeInit(() => {
        introControllerRef.current = new HoldAndWinIntroController(
          spineContainerRef.current,
          dimensionsContext,
          dispatch,
          symbolHeight,
          overlayRef,
          buttonRef,
          t
        );
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle dimension changes
  useEffect(() => {
    if (introControllerRef.current && symbolHeight && dimensionsContext) {
      introControllerRef.current.updateDimensions(
        dimensionsContext,
        symbolHeight
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbolHeight]);

  // Play animation when triggered
  useEffect(() => {
    if (introControllerRef.current && showHoldAndWinIntro) {
      introControllerRef.current.startSequence();
    }
  }, [showHoldAndWinIntro]);

  // Handle user click to continue animation
  const handleBackgroundClick = useCallback(
    (evt) => {
      if (evt && typeof evt.preventDefault === "function") evt.preventDefault();
      if (evt && typeof evt.stopPropagation === "function")
        evt.stopPropagation();
      if (evt && typeof evt.stopImmediatePropagation === "function")
        evt.stopImmediatePropagation();

      if (introControllerRef.current && showHoldAndWinIntro) {
        const accepted = introControllerRef.current.handleUserClick();
        if (accepted) {
          soundManager.play(SOUND_NAME.button_click);
        }
      }
    },
    [showHoldAndWinIntro]
  );

  // Add spacebar handling
  useEffect(() => {
    const handleKeyDown = (event) => {
      // Only handle spacebar
      if (event.code !== "Space" && event.key !== " " && event.keyCode !== 32) {
        return;
      }

      // Ignore auto-repeat (holding the key)
      if (event.repeat) {
        event.preventDefault();
        if (typeof event.stopPropagation === "function")
          event.stopPropagation();
        if (typeof event.stopImmediatePropagation === "function")
          event.stopImmediatePropagation();
        return;
      }

      // Only handle when intro is showing AND controller exists
      if (!showHoldAndWinIntro || !introControllerRef.current) {
        return;
      }

      // Prevent event from reaching other handlers
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      try {
        const accepted = introControllerRef.current.handleUserClick();
        if (accepted) {
          soundManager.play(SOUND_NAME.button_click);
        }
      } catch (error) {
        // Emergency recovery - try to finish animation
        if (
          introControllerRef.current &&
          introControllerRef.current.finishAnimation
        ) {
          introControllerRef.current.finishAnimation();
        }
      }
    };

    // Use capture phase to ensure we get the event first
    if (showHoldAndWinIntro) {
      document.addEventListener("keydown", handleKeyDown, {
        capture: true,
        passive: false, // Allow preventDefault
      });
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown, { capture: true });
    };
  }, [showHoldAndWinIntro]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (introControllerRef.current) {
        introControllerRef.current.cleanup();
      }
    };
  }, []);

  if (!dimensions || !dimensions.width || !dimensions.height) {
    return null;
  }

  return (
    <Container
      name="hold_and_win_intro"
      x={dimensions.width / 2}
      y={dimensions.height / 2}
      sortableChildren={true}
      zIndex={9}
      ref={spineContainerRef}
    >
      <Overlay
        ref={overlayRef}
        dimensions={dimensions}
        interactive={showHoldAndWinIntro}
        onPointerDown={handleBackgroundClick}
        x={-dimensions.width / 2}
        y={-dimensions.height / 2}
        fadeDuration={350}
      />

      <TextButton ref={buttonRef} y={dimensions.height / 2} text="" />
    </Container>
  );
};

export default HoldAndWinIntroDialog;
